import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';
import { parse as parseQuery } from 'qs';

import { PortalWithdrawal } from 'playup-web-core';

import { LoaderFixed } from '../../../components/Loader';
import NoBankAccount from './NoBankAccount';
import WithBankAccount from './WithBankAccount';
import PendingWithdrawals from './PendingWithdrawals';
import paginationBuilder from '../../../utils/paginationBuilder';
import useNotify from '../../../hooks/useNotify';
import PortalSessionContext from '../../../contexts/PortalSessionContext/PortalSessionContext';
import { deleteBankDocument, getPendingWithdrawRequests } from '../../../api/users';
import { cancelWithdrawal } from '../../../api/withdraw';
import { parseApiError } from '../../../utils/parseApiError';
import GeocomplyContext from '../../../contexts/GeocomplyContext/GeocomplyContext';
import appConfig from '../../../appConfig';
import VerifyIdLink from './VerifyIdLink';
import CashTransactionLink from './CashTransactionLink';
import ProfileLink from './ProfileLink';

const Withdraw = () => {
	const { user, refreshPortalUser } = React.useContext(PortalSessionContext);
	const [loadingWithdrawals, setLoadingWithdrawals] = React.useState<boolean>(true);
	const [withdrawals, setWithdrawals] = React.useState<Withdrawal[]>([]);
	const [pagination, setPagination] = React.useState<Array<PaginationLink>>([]);
	const { requestGeolocation } = React.useContext(GeocomplyContext);
	const timer = React.useRef();
	const notify = useNotify();
	const location = useLocation();
	const { page = '1' } = parseQuery(location.search, {
		ignoreQueryPrefix: true,
	});
	const fetchPendingWithdrawals = React.useCallback(() => {
		refreshPortalUser();

		getPendingWithdrawRequests(page as string)
			.then((response) => {
				setWithdrawals(response.data);
				setPagination(paginationBuilder(response.meta.pagination));
			})
			.catch((error) => {
				notify({
					type: 'danger',
					title: 'Error',
					message: parseApiError(error.errors),
					timeout: 5000,
				});
			})
			.finally(() => {
				setLoadingWithdrawals(false);
			});
	}, [page, notify, refreshPortalUser]);
	React.useEffect(fetchPendingWithdrawals, [fetchPendingWithdrawals, page]);

	React.useEffect(() => {
		if (timer.current) clearInterval(timer.current);
		const hasAutoWithdrawal = withdrawals.findIndex((withdrawal) => withdrawal.is_auto) > -1;
		if (!hasAutoWithdrawal) return;
		//@ts-ignore
		timer.current = setInterval(() => {
			fetchPendingWithdrawals();
		}, 2000);
		return () => {
			clearInterval(timer.current);
		};
	}, [fetchPendingWithdrawals, withdrawals]);

	React.useEffect(() => {
		if (appConfig.regions.isUS) requestGeolocation('withdrawal request');
	}, [requestGeolocation]);

	const onCancelWithdrawal = (withdrawalId: number) => {
		cancelWithdrawal(withdrawalId)
			.then(() => {
				fetchPendingWithdrawals();
				notify({
					type: 'success',
					title: 'Success',
					message: 'Your withdrawal has been cancelled',
					timeout: 5000,
				});
			})
			.catch((error) => {
				notify({
					type: 'danger',
					title: 'Error',
					message: parseApiError(error.errors),
					timeout: 5000,
				});
			});
	};

	const onBankAccountRemoved = () => {
		if (user?.user_bank_account?.id) {
			deleteBankDocument(user.user_bank_account.id)
				.then(() => {
					fetchPendingWithdrawals();
					refreshPortalUser();
					notify({
						type: 'success',
						title: 'Success',
						message: 'You can now proceed to enter your new bank account.',
						timeout: 5000,
					});
				})
				.catch((error) => {
					notify({
						type: 'danger',
						title: 'Error',
						message: parseApiError(error.errors),
						timeout: 5000,
					});
				});
		}
	};

	if (!user) {
		return <LoaderFixed />;
	}
	return (
		<>
			<Helmet title="Withdraw" />
			<PortalWithdrawal
				user={user}
				withdrawals={withdrawals}
				pagination={pagination}
				sending={false}
				loadingWithdrawals={loadingWithdrawals}
				fetchPendingWithdrawals={fetchPendingWithdrawals}
				onCancelWithdrawal={onCancelWithdrawal}
				onBankAccountRemoved={onBankAccountRemoved}
				WithBankAccount={WithBankAccount}
				NoBankAccount={NoBankAccount}
				PendingWithdrawals={PendingWithdrawals}
				ProfileLink={ProfileLink}
				VerifyIdLink={VerifyIdLink}
				CashTransactionLink={CashTransactionLink}
			/>
		</>
	);
};

export default Withdraw;
